import type { Deck } from '../types';

interface Props {
  deck: Deck;
  due: number;
  t: (key: string) => string;
  onOpen: () => void;
  onQuiz: () => void;
  onDelete: () => void;
}

const ORIGIN_META: Record<string, { icon: string; label: string }> = {
  manual: { icon: 'ri-pencil-line', label: 'Créé à la main' },
  notes: { icon: 'ri-sticky-note-line', label: 'Depuis mes notes' },
  document: { icon: 'ri-file-text-line', label: 'Depuis un document' },
};

export default function DeckCard({ deck, due, t, onOpen, onQuiz, onDelete }: Props) {
  const origin = ORIGIN_META[deck.origin || 'manual'] || ORIGIN_META.manual;
  const progress = deck.cards.length > 0
    ? Math.round(((deck.cards.length - due) / deck.cards.length) * 100)
    : 0;

  return (
    <article className="group bg-background-50 border border-background-200/50 rounded-2xl p-4 flex flex-col hover:border-foreground-300/60 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <span className="w-10 h-10 rounded-xl bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
          <i className="ri-stack-line text-base"></i>
        </span>
        <button
          onClick={onDelete}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-300 hover:text-red-600 hover:bg-red-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
          title={t('cards.deleteDeck')}
        >
          <i className="ri-delete-bin-line text-xs"></i>
        </button>
      </div>

      <h3 className="text-sm font-bold font-heading text-foreground-950 mt-2.5 truncate">{deck.name}</h3>
      <p className="text-[11px] text-foreground-500 truncate">
        {deck.subject}{deck.chapter ? ` · ${deck.chapter}` : ''}
      </p>

      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-semibold">
          {deck.cards.length} {t('cards.cards').toLowerCase()}
        </span>
        {due > 0 ? (
          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-bold">
            {due} {t('cards.due').toLowerCase()}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold">
            <i className="ri-check-line mr-0.5"></i>à jour
          </span>
        )}
        <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] inline-flex items-center gap-1">
          <i className={`${origin.icon} text-[10px]`}></i>
          {origin.label}
        </span>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-foreground-400 mb-1">
          <span>Maîtrise</span>
          <span className="tabular-nums font-semibold text-foreground-600">{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-background-100 overflow-hidden">
          <div className="h-full rounded-full bg-secondary-500 transition-all" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={onOpen}
          className="flex-1 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70"
        >
          {t('cards.cardsMode')}
        </button>
        <button
          onClick={onQuiz}
          disabled={deck.cards.length < 2}
          className="flex-1 px-3 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-40 flex items-center justify-center gap-1"
        >
          <i className="ri-play-fill"></i>
          {t('cards.quiz')}
        </button>
      </div>
    </article>
  );
}